import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Grid,
    Switch,
    TextField,
    Typography,
    Dialog,
    DialogContent,
    Link,
    List, ListItem, ListItemIcon, ListItemText,
    IconButton,
    InputAdornment
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { validatePassword } from "../validations.ts";
import { userInformationAtom } from "../../../Services/_states/auth.js";
import { useRecoilValue } from "recoil";
import { useNavigate } from "react-router";
import { useSnackBar } from "../../FlowBuilder/Common/SnackBar/index.tsx";
import { ManagementActions } from "../../../Services/_actions/index.js";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const ChangePasssword: React.FC = () => {
    const loggedInUser = useRecoilValue(userInformationAtom);
    const navigate = useNavigate();
    const [confirmPassword, setConfirmPassword] = useState("");
    const [validationErrors, setValidationErrors] = useState({
        password: '',
        confirm_password: '',
    });
    const actions = ManagementActions();
    const { showSnackBar } = useSnackBar();
    const [formData, setFormData] = useState({
        password: '',
        confirm_password: '',
    });
    const [passwordRules, setPasswordRules] = useState({
        minLength: false,
        uppercase: false,
        lowercase: false,
        number: false,
        specialChar: false,
        noCommonWords: true,
        noUsernameSubstring: false,
        // expiryEnforced: true,
        //  lockoutEnforced: true,
        // passwordReuseLimitExceeded: false
    });
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const errors: any = {};
        const password = formData?.password;
        const { isValid, rules } = validatePassword(password, loggedInUser?.username || "");
        if (!isValid) {
            errors.password = "Password does not meet all reuirements"
        }

        if (password !== formData?.confirm_password) {
            errors.confirm_password = "Passwords do not match.";
        }

        setValidationErrors(errors);

        if (Object.keys(errors).length === 0) {
            try {
                let res = await actions.changeUserPassword({ password: password, id: loggedInUser?.id });
                if (res.status === 200) {
                    showSnackBar(res?.data?.data, "success");
                    window.location.href = "/#/logout";
                } else {
                    showSnackBar('Password must not match the last 8 used passwords.', 'error');
                }
            } catch (error) {
                showSnackBar(error?.toString(), 'error');
            }
        } else {
            showSnackBar('Password does not meet all reuirements', 'warning');

        }
    };
    const handleInputChange = (field: string, value: string) => {
        setFormData({ ...formData, [field]: value });
        if (field === "password") {
            const { isValid, rules } = validatePassword(value, loggedInUser?.username || "");
            setPasswordRules(rules);
            setValidationErrors((prevErrors) => ({
                ...prevErrors,
                password: isValid ? "" : "Password does not meet all requirements",
            }));
        }

    };

    return (
        <Box
            sx={{
                padding: "20px",
                maxWidth: "500px",
                margin: "auto",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
        >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                Change Password
            </Typography>
            <form onSubmit={handleSubmit}>
                <TextField
                    label={
                        <Typography component="span">
                            New Password <span style={{ color: 'red' }}>*</span>
                        </Typography>
                    }
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={formData.password}
                    onChange={(e) => {
                        handleInputChange("password", e.target.value)
                        setValidationErrors({ ...validationErrors, password: '' });
                    }}
                    sx={{ marginBottom: 2 }}
                    margin="dense"
                    variant="outlined"
                    size="small"
                    error={!!validationErrors.password}
                    helperText={validationErrors.password}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}

                />
                <Typography variant="subtitle1" style={{ marginTop: "5px" }}>
                    Password must meet the following requirements:
                </Typography>
                <Box mt={1} mb={1}>
                    <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.minLength ? 'success.main' : 'error.main'}>
                        {passwordRules?.minLength ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                        At least 12 characters
                    </Typography>
                    <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.uppercase ? 'success.main' : 'error.main'}>
                        {passwordRules?.uppercase ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                        At least one uppercase letter (A-Z)
                    </Typography>
                    <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.lowercase ? 'success.main' : 'error.main'}>
                        {passwordRules?.lowercase ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                        At least one lowercase letter (a-z)
                    </Typography>
                    <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.number ? 'success.main' : 'error.main'}>
                        {passwordRules?.number ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                        At least one number (0-9)
                    </Typography>
                    <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.specialChar ? 'success.main' : 'error.main'}>
                        {passwordRules?.specialChar ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                        At least one special character (@$!%*?&)
                    </Typography>
                    <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.noCommonWords ? 'success.main' : 'error.main'}>
                        {passwordRules?.noCommonWords ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                        Must not include common words (e.g., 'password', 'admin')
                    </Typography>
                    <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.noUsernameSubstring ? 'success.main' : 'error.main'}>
                        {passwordRules?.noUsernameSubstring ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                        Must not contain your username
                    </Typography>
                </Box>
                {/* <List dense>
                    <ListItem>
                        <ListItemIcon>
                            {passwordRules.minLength ? (
                                <CheckCircleIcon color="success" />
                            ) : (
                                <CancelIcon color="error" />
                            )}
                        </ListItemIcon>
                        <ListItemText primary="At least 12 characters long" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            {passwordRules.uppercase ? (
                                <CheckCircleIcon color="success" />
                            ) : (
                                <CancelIcon color="error" />
                            )}
                        </ListItemIcon>
                        <ListItemText primary="At least one uppercase letter (A–Z)" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            {passwordRules.lowercase ? (
                                <CheckCircleIcon color="success" />
                            ) : (
                                <CancelIcon color="error" />
                            )}
                        </ListItemIcon>
                        <ListItemText primary="At least one lowercase letter (a–z)" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            {passwordRules.number ? (
                                <CheckCircleIcon color="success" />
                            ) : (
                                <CancelIcon color="error" />
                            )}
                        </ListItemIcon>
                        <ListItemText primary="At least one number (0–9)" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            {passwordRules.specialChar ? (
                                <CheckCircleIcon color="success" />
                            ) : (
                                <CancelIcon color="error" />
                            )}
                        </ListItemIcon>
                        <ListItemText primary="At least one special character (e.g., !@#$%^&*())" />
                    </ListItem>

                    <ListItem>
                        <ListItemIcon>
                            {passwordRules.noCommonWords ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                        </ListItemIcon>
                        <ListItemText primary="Must not include common words (e.g., 'password', 'admin')" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            {passwordRules.noUsernameSubstring ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                        </ListItemIcon>
                        <ListItemText primary="Must not contain your username" />
                    </ListItem>

                </List> */}

                <TextField
                    label={
                        <Typography component="span">
                            Confirm Password <span style={{ color: 'red' }}>*</span>
                        </Typography>
                    }
                    type="password"
                    fullWidth
                    value={formData.confirm_password}
                    onChange={(e) => {
                        setFormData({ ...formData, confirm_password: e.target.value });
                        setValidationErrors({ ...validationErrors, confirm_password: '' });
                    }}
                    sx={{ marginBottom: 2 }}
                    margin="dense"
                    variant="outlined"
                    size="small"
                    error={!!validationErrors?.confirm_password}
                    helperText={validationErrors?.confirm_password}
                />
                <Box display="flex" flexDirection="column" alignItems="stretch" gap={2}>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="outlined" onClick={() => navigate(-1)} size="small">
                            Cancel
                        </Button>
                        <Button variant="contained" type="submit" color="primary" size="small"
                        >
                            Change Password
                        </Button>
                    </Box>
                </Box>
            </form>
        </Box>
    );
};

export default ChangePasssword;
